/**
 * ITU-R BT.601 luma coefficients for converting RGB to greyscale.
 * Weighted to match human perception: green contributes most, blue the least.
 * Formula: grey = LUMA_R·R + LUMA_G·G + LUMA_B·B
 */
const LUMA_R = 0.299
const LUMA_G = 0.587
const LUMA_B = 0.114

/**
 * Byte offsets for each channel within a flat RGBA ImageData buffer.
 * Each pixel occupies 4 consecutive bytes, so pixel i starts at index i*4.
 * e.g. srcData[i * 4 + R] reads the red channel of pixel i.
 */
const R = 0
const G = 1
const B = 2
const A = 3

/**
 * Single-pass preprocessing pipeline (1 getImageData + 1 putImageData):
 *
 *   Step 1 — Grayscale + integral image (built in the same loop, O(n))
 *   Step 2 — Adaptive binarization (Sauvola-style local threshold)
 *   Step 3 — Table line removal (long ink runs → erased)
 *   Step 4 — Write result
 *
 * Previously these were three separate functions each calling getImageData and
 * putImageData, wasting two GPU↔CPU round-trips per page.
 */
export function preprocessForOcr(
  ctx: CanvasRenderingContext2D,
  radius = 15,
  k = 0.15,
  lineMinFraction = 0.25
): void {
  const { width, height } = ctx.canvas
  const src = ctx.getImageData(0, 0, width, height)
  const dst = ctx.createImageData(width, height)
  const srcData = src.data
  const dstData = dst.data

  // ── Step 1: Grayscale + integral image ──────────────────────────────────
  const integral = new Float64Array((width + 1) * (height + 1))
  const grey = new Uint8Array(width * height)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x
      const grayValue = Math.round(
        LUMA_R * srcData[i * 4 + R] +
          LUMA_G * srcData[i * 4 + G] +
          LUMA_B * srcData[i * 4 + B]
      )
      grey[i] = grayValue
      integral[(y + 1) * (width + 1) + (x + 1)] =
        grayValue +
        integral[y * (width + 1) + (x + 1)] +
        integral[(y + 1) * (width + 1) + x] -
        integral[y * (width + 1) + x]
    }
  }

  // ── Step 2: Adaptive binarization ───────────────────────────────────────
  const ink = new Uint8Array(width * height)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const x0 = Math.max(0, x - radius)
      const y0 = Math.max(0, y - radius)
      const x1 = Math.min(width - 1, x + radius)
      const y1 = Math.min(height - 1, y + radius)
      const count = (x1 - x0 + 1) * (y1 - y0 + 1)
      const sum =
        integral[(y1 + 1) * (width + 1) + (x1 + 1)] -
        integral[y0 * (width + 1) + (x1 + 1)] -
        integral[(y1 + 1) * (width + 1) + x0] +
        integral[y0 * (width + 1) + x0]
      ink[y * width + x] = grey[y * width + x] < (sum / count) * (1 - k) ? 1 : 0
    }
  }

  // ── Step 3: Table line removal ───────────────────────────────────────────
  const toErase = new Uint8Array(width * height)
  const minHorizontalRun = Math.round(width * lineMinFraction)
  const minVerticalRun = Math.round(height * lineMinFraction)

  for (let y = 0; y < height; y++) {
    let run = 0
    let start = 0
    for (let x = 0; x <= width; x++) {
      if (x < width && ink[y * width + x]) {
        if (run === 0) start = x
        run++
      } else {
        if (run >= minHorizontalRun)
          for (let rx = start; rx < start + run; rx++)
            toErase[y * width + rx] = 1
        run = 0
      }
    }
  }

  for (let x = 0; x < width; x++) {
    let run = 0
    let start = 0
    for (let y = 0; y <= height; y++) {
      if (y < height && ink[y * width + x]) {
        if (run === 0) start = y
        run++
      } else {
        if (run >= minVerticalRun)
          for (let ry = start; ry < start + run; ry++)
            toErase[ry * width + x] = 1
        run = 0
      }
    }
  }

  // ── Step 4: Write output ─────────────────────────────────────────────────
  for (let i = 0; i < width * height; i++) {
    const out = ink[i] && !toErase[i] ? 0 : 255
    dstData[i * 4 + R] = dstData[i * 4 + G] = dstData[i * 4 + B] = out
    dstData[i * 4 + A] = 255
  }

  ctx.putImageData(dst, 0, 0)
}
